import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    checkType,
    CtyButton,
    CtyShowToast,
    JsonFormBuilder,
    FileUpload,
    formatFileSize,
    getFileType,
    UploadedFileType,
    ClaimDataRenderer,
    ClaimScoreGeneratorRenderer,
    InvoiceMatcherRenderer,
    UXGenieTemplateRenderer,
    useUxgenieTemplateHtmlResolver
} from '@circuitry-ai/doc-ui';
import {
    useUploadInternalActionMutation,
    useGetAiworkersPresignedUrlMutation,
    useUploadFileToS3Mutation,
    useActionUploadInternalActionMutation,
    useAppDispatch,
    updateAgentMessageState,
    useGetAiWorkerUsageByIdMutation,
    useEntityResourcePermissions,
} from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import { Chip, Divider, Input, Select, SelectItem, Tabs, Tab, Textarea } from '@nextui-org/react';
import { ImageFraudChecker } from './imagefraudchecker';

// Utility functions to reduce code duplication
const safeParsePayload = (payload: any): any => {
    if (!payload) return {};
    return typeof payload === 'string' ? JSON.parse(payload) : payload;
};

const hasRequiredPattern = (value: any): boolean => {
    return typeof value === 'string' && /^\$\{.+\}$/.test(value);
};

const getApiSpec = (action: any) => {
    const attributes = action?.attributes || action?.sources_attrs || [];
    return attributes.find((attr: any) => attr.attr_name === 'api_spec')?.attr_json;
};

const AUTO_FILLED_FILE_KEYS = ['source_id', 'source_extension', 'file_name'];

/** Returns the top-level key whose value is an array of file-like template objects, or null. */
const getFileArrayKey = (parsedPayload: any): string | null => {
    if (!parsedPayload || typeof parsedPayload !== 'object') return null;
    for (const key of Object.keys(parsedPayload)) {
        const val = parsedPayload[key];
        if (!Array.isArray(val) || val.length === 0) continue;
        const first = val[0];
        if (first && typeof first === 'object' && AUTO_FILLED_FILE_KEYS.some((k) => first[k] !== undefined)) return key;
    }
    return null;
};

/** Returns file-array template info: key, first item, its keys, and extra keys (excluding auto-filled). */
const getFileArrayTemplateInfo = (parsedPayload: any) => {
    const fileArrayKey = getFileArrayKey(parsedPayload);
    const templateSource = fileArrayKey ? parsedPayload[fileArrayKey]?.[0] : null;
    const keysInTemplate = templateSource ? Object.keys(templateSource) : [];
    const extraKeys = keysInTemplate.filter((k: string) => !AUTO_FILLED_FILE_KEYS.includes(k));
    return { fileArrayKey, templateSource, keysInTemplate, extraKeys };
};

const filterRequiredFields = (payload: any) => {
    return Object.entries(payload)
        .filter(([key, value]) => hasRequiredPattern(value))
        .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {} as Record<string, any>);
};

// Helper function to find arrays with templated values
const findArrayFields = (obj: any): Record<string, any[]> => {
    const arrayFields: Record<string, any[]> = {};

    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return arrayFields;
    }

    for (const [key, value] of Object.entries(obj)) {
        if (Array.isArray(value) && value.length > 0) {
            if (value.every(item => hasRequiredPattern(item))) {
                arrayFields[key] = value;
            }
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nestedArrays = findArrayFields(value);
            Object.assign(arrayFields, nestedArrays);
        }
    }

    return arrayFields;
};

// New recursive function to find all templated fields in nested objects
const findTemplatedFields = (obj: any, prefix: string = ''): Record<string, string> => {
    const templatedFields: Record<string, string> = {};

    if (!obj || typeof obj !== 'object') {
        return templatedFields;
    }

    // Handle arrays - iterate through each item and process recursively
    if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
            if (item && typeof item === 'object') {
                const arrayPrefix = prefix ? `${prefix}[${index}]` : `[${index}]`;
                const nestedFields = findTemplatedFields(item, arrayPrefix);
                Object.assign(templatedFields, nestedFields);
            }
        });
        return templatedFields;
    }

    // Handle objects
    for (const [key, value] of Object.entries(obj)) {
        const fieldPath = prefix ? `${prefix}.${key}` : key;

        if (hasRequiredPattern(value)) {
            // Extract the variable name from ${variableName}
            const match = (value as string).match(/^\$\{(.+)\}$/);
            const variableName = match ? match[1] : key;
            templatedFields[variableName] = value as string;
        } else if (value && typeof value === 'object') {
            // Recursively search nested objects and arrays
            const nestedFields = findTemplatedFields(value, fieldPath);
            Object.assign(templatedFields, nestedFields);
        }
    }

    return templatedFields;
};

// Helper function to process templated field
const processTemplatedField = (value: string, key: string, formData: Record<string, any>): any => {
    const regex = /^\$\{(.+)\}$/;
    const match = regex.exec(value);
    const variableName = match ? match[1] : key;

    if (variableName in formData && formData[variableName] !== undefined && formData[variableName] !== '') {
        return formData[variableName];
    }
    return undefined; // Skip field if no user value provided
};

// Enhanced function to create nested payload while preserving structure
const createNestedPayloadRecursive = (originalPayload: any, formData: Record<string, any>): any => {
    if (!originalPayload || typeof originalPayload !== 'object') {
        return originalPayload;
    }

    if (Array.isArray(originalPayload)) {
        return originalPayload.map(item => createNestedPayloadRecursive(item, formData));
    }

    const result: any = {};

    for (const [key, value] of Object.entries(originalPayload)) {
        if (Array.isArray(formData[key]) && Array.isArray(value)) {
            const filteredArray = formData[key].filter((item: string) => item.trim() !== '');
            if (filteredArray.length > 0) {
                result[key] = filteredArray;
            }
        } else if (hasRequiredPattern(value)) {
            const processedValue = processTemplatedField(value as string, key, formData);
            if (processedValue !== undefined) {
                result[key] = processedValue;
            }
        } else if (value && typeof value === 'object') {
            const nestedResult = createNestedPayloadRecursive(value, formData);
            if (nestedResult && Object.keys(nestedResult).length > 0) {
                result[key] = nestedResult;
            }
        } else {
            result[key] = value;
        }
    }

    return result;
};

const createNestedPayload = (parsedPayload: any, formData: Record<string, any>) => {
    const nestedPayload: any = {};
    for (const key of Object.keys(parsedPayload)) {
        if (hasRequiredPattern(parsedPayload[key])) {
            if (key in formData && formData[key as keyof typeof formData] !== undefined) {
                nestedPayload[key] = formData[key as keyof typeof formData];
            } else {
                nestedPayload[key] = parsedPayload[key];
            }
        }
    }
    return nestedPayload;
};

/** Accept flat `{ vin: "..." }` or a full nested payload from the textarea. */
const mergeTextareaPayloadWithTemplate = (
    userPayload: Record<string, unknown>,
    samplePayload: unknown,
): unknown => {
    const template = safeParsePayload(samplePayload);
    const templatedFields = findTemplatedFields(template);
    const flatFieldNames = new Set(Object.keys(templatedFields));
    const userKeys = Object.keys(userPayload);
    const isFlatFormInput =
        userKeys.length > 0 && userKeys.every((key) => flatFieldNames.has(key));

    if (isFlatFormInput) {
        return createNestedPayloadRecursive(template, userPayload as Record<string, any>);
    }

    return userPayload;
};

function CacheHitNotice({ cache }: { readonly cache?: { hit?: boolean; age_seconds?: number } }) {
    const { t } = useTranslation();
    if (cache?.hit !== true) return null;
    const age = Number(cache.age_seconds);
    const label = Number.isFinite(age)
        ? t('Served from cache ({{seconds}}s old)', { seconds: Math.trunc(age) })
        : t('Served from cache');
    return (
        <Chip size="sm" variant="flat" color="success" className="mb-2">
            {label}
        </Chip>
    );
}

export interface ActionExecuteProps { }
// Text File Preview Component
const TextFilePreview = ({ previewUrl, fileName, fileSize, fileExtension }: { previewUrl: string, fileName: string, fileSize: string, fileExtension: string }) => {
    const [textContent, setTextContent] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();

    useEffect(() => {
        const fetchTextContent = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(previewUrl);
                const text = await response.text();
                setTextContent(text);
                setError(null);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : t('Failed to load file content');
                setError(errorMessage);
            } finally {
                setIsLoading(false);
            }
        };

        void fetchTextContent();
    }, [previewUrl, t]);

    if (isLoading) {
        return (
            <div className="w-full border rounded-lg bg-default-100 flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
                <div className="text-center">
                    <Icon icon="mdi:loading" className="w-12 h-12 text-primary animate-spin mx-auto mb-2" />
                    <p className="text-sm text-default-500">{t('Loading file content...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full border rounded-lg bg-default-100 flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
                <div className="text-center">
                    <Icon icon="mdi:alert-circle" className="w-12 h-12 text-danger mx-auto mb-2" />
                    <p className="text-sm text-danger">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full border rounded-lg bg-default-100 overflow-hidden" style={{ minHeight: 'calc(100vh - 200px)' }}>
            <div className="bg-default-100 px-4 py-3 border-b border-default-200 flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-default-600">{fileName}</p>
                    <p className="text-xs text-default-500">{fileSize}</p>
                </div>
                <Icon icon="mdi:file-code" className="w-6 h-6 text-default-500" />
            </div>
            <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                <pre className="text-sm text-default-700 whitespace-pre-wrap break-words font-mono bg-content1 p-4 rounded border border-default-200">
                    {textContent}
                </pre>
            </div>
        </div>
    );
};

export function ActionExecute(props: ActionExecuteProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { action } = location?.state ?? {};
    const actionSourceId = action?.source_id as string | undefined;
    const actionResourceId = action?.source_id || action?.aiworker_id;
    const { canUpdate } = useEntityResourcePermissions('action', actionResourceId);

    const [formData, setFormData] = useState<Record<string, any>>({});
    const [apiResponse, setApiResponse] = useState<any>('');
    const [uploadedFile, setUploadedFile] = useState<UploadedFileType | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFileType[]>([]);
    const [uploadedFilesInfo, setUploadedFilesInfo] = useState<{ source_id: string; source_extension: string; file_name: string }[]>([]);
    const [extraFieldsByFileIndex, setExtraFieldsByFileIndex] = useState<Record<string, string>[]>([]);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [inputType, setInputType] = useState<'form' | 'file' | 'json'>('form');
    const [fileNameField, setFileNameField] = useState<string | null>(null);
    const [selectedAgentIndex, setSelectedAgentIndex] = useState<string | number>('view-all');

    const [, { isLoading }] = useUploadInternalActionMutation();
    const [actionUploadInternalAction, { isLoading: isActionLoading }] = useActionUploadInternalActionMutation();
    const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
    const [uploadtos3] = useUploadFileToS3Mutation();
    const [getTemplateById, { isLoading: isLoadingTemplate }] = useGetAiWorkerUsageByIdMutation();
    const { resolveHtml } = useUxgenieTemplateHtmlResolver();
    const dispatch = useAppDispatch();

    // Template state for saved UXGenie templates
    const [savedTemplate, setSavedTemplate] = useState<{ actionName: string; code: string; schema: string; savedAt: string } | null>(null);
    const [useCustomTemplate, setUseCustomTemplate] = useState(false);
    const [formInputModeTab, setFormInputModeTab] = useState<'form' | 'json'>('form');
    const [rawPayloadText, setRawPayloadText] = useState('');
    const prevActionSourceIdRef = useRef<string | undefined>();
    const formInitActionIdRef = useRef<string | undefined>();

    useEffect(() => {
        if (!actionSourceId) return;
        if (prevActionSourceIdRef.current === actionSourceId) return;
        prevActionSourceIdRef.current = actionSourceId;
        formInitActionIdRef.current = undefined;
        setFormInputModeTab('form');
        setRawPayloadText('');
    }, [actionSourceId]);

    // Load saved template from action attributes on mount
    useEffect(() => {
        if (action) {
            // Check both attributes and sources_attrs for compatibility
            const attrs = action.attributes || action.sources_attrs || [];

            // Find the uxgenie_template attribute
            const templateAttr = attrs.find(
                (attr: any) => attr.attr_name === 'uxgenie_template'
            );

            if (templateAttr) {
                // New format: attr_value contains usage_id, need to fetch from API
                if (templateAttr.attr_value) {
                    const usageId = templateAttr.attr_value;
                    console.log('📦 Fetching UXGenie template from API for usage_id:', usageId);

                    getTemplateById(usageId)
                        .unwrap()
                        .then(async (response) => {
                            console.log('✅ Template fetched from API:', response);

                            // Handle array or object response
                            let templateData = response;
                            if (Array.isArray(response) && response.length > 0) {
                                templateData = response[0];
                            } else if (response?.data) {
                                templateData = response.data;
                            }

                            const outputData = templateData?.action_output || templateData?.output_data;

                            // Extract generated_code from action_output or output_data
                            let generatedCode = templateData?.action_output?.generated_code ||
                                templateData?.output_data?.generated_code;

                            if (!generatedCode && outputData?.output_type === 'html' && outputData?.storage === 's3') {
                                const aiworker_id = templateData?.aiworker_id || action?.aiworker_id;
                                if (aiworker_id) {
                                    try {
                                        generatedCode = await resolveHtml({ aiworker_id, aiworker_usage_id: usageId, fileName: outputData.html_file_name });
                                    } catch (error) {
                                        console.error('❌ Failed to resolve saved HTML template from storage:', error);
                                    }
                                }
                            }

                            if (generatedCode) {
                                console.log('✅ Template code extracted, length:', generatedCode.length);
                                setSavedTemplate({
                                    actionName: action.name,
                                    code: generatedCode,
                                    schema: '', // Not needed for rendering
                                    savedAt: templateData?.used_at || new Date().toISOString()
                                });
                                // Automatically switch to template view when template is loaded
                                setUseCustomTemplate(true);
                                console.log('✅ Template view enabled by default');
                            } else {
                                console.error('❌ No generated_code found in template response');
                            }
                        })
                        .catch((error) => {
                            console.error('❌ Failed to fetch template from API:', error);
                        });
                }
                // Legacy format: attr_json contains the template directly
                else if (templateAttr.attr_json) {
                    const templateData = templateAttr.attr_json;
                    setSavedTemplate({
                        actionName: action.name,
                        code: templateData.code,
                        schema: templateData.schema,
                        savedAt: templateData.savedAt
                    });
                    // Automatically switch to template view for legacy format too
                    setUseCustomTemplate(true);
                    console.log('✅ Template view enabled by default (legacy format)');
                }
            } else {
                // Legacy: Check localStorage for backwards compatibility
                const templateKey = `uxgenie_template_${action.name.replace(/\s+/g, '_')}`;
                const savedData = localStorage.getItem(templateKey);
                if (savedData) {
                    try {
                        const parsed = JSON.parse(savedData);
                        setSavedTemplate(parsed);
                        // Automatically switch to template view for localStorage templates
                        setUseCustomTemplate(true);
                        console.log('✅ Template view enabled by default (localStorage)');
                    } catch (e) {
                        console.error('Failed to parse saved template:', e);
                    }
                }
            }
        }
    }, [action, getTemplateById]);

    // Manage preview URL for uploaded files
    useEffect(() => {
        if (uploadedFile?.file) {
            const url = URL.createObjectURL(uploadedFile.file);
            setPreviewUrl(url);

            // Cleanup function to revoke the object URL
            return () => {
                URL.revokeObjectURL(url);
                setPreviewUrl(null);
            };
        } else {
            setPreviewUrl(null);
        }
    }, [uploadedFile]);

    useEffect(() => {
        if (!action || !actionSourceId) return;
        if (formInitActionIdRef.current === actionSourceId) return;
        formInitActionIdRef.current = actionSourceId;

        {
            const apiSpec = getApiSpec(action);
            const samplePayload = apiSpec?.sample_payload || apiSpec?.payload;

            if (samplePayload) {
                try {
                    const parsedPayload = safeParsePayload(samplePayload);

                    // Find arrays with templated values
                    const arrayFields = findArrayFields(parsedPayload);
                    const templatedFields = findTemplatedFields(parsedPayload);
                    const topLevelRequiredFields = filterRequiredFields(parsedPayload);
                    const hasSourceExtension = 'source_extension' in templatedFields;

                    // Determine input type based on payload structure
                    if ((parsedPayload.source_extension && hasRequiredPattern(parsedPayload.source_extension)) || hasSourceExtension) {
                        // If payload contains source_extension with required pattern, use file input
                        setInputType('file');

                        // Track which field represents the filename
                        let fileNameVariableName = null;
                        if (parsedPayload.file_name) {
                            const match = parsedPayload.file_name.match(/^\$\{(.+)\}$/);
                            fileNameVariableName = match ? match[1] : 'file_name';
                        }
                        setFileNameField(fileNameVariableName);

                        const initialData: Record<string, any> = {
                            ...Object.keys(templatedFields)
                                .filter(key => key !== fileNameVariableName)
                                .reduce((acc, key) => {
                                    acc[key] = '';
                                    return acc;
                                }, {} as Record<string, any>),
                            ...Object.entries(topLevelRequiredFields)
                                .filter(([key, value]) => {
                                    const isTemplated = hasRequiredPattern(value);
                                    const isExplicit = key === 'source_extension';
                                    const isNotFileName = key !== 'file_name';
                                    return (isTemplated || isExplicit) && isNotFileName;
                                })
                                .reduce((acc, [key]) => {
                                    acc[key] = '';
                                    return acc;
                                }, {} as Record<string, any>),
                            ...Object.entries(arrayFields).reduce((acc, [key, value]) => {
                                acc[key] = value.map(() => '');
                                return acc;
                            }, {} as Record<string, any>)
                        };
                        setFormData(initialData);
                    } else if (Object.keys(templatedFields).length > 0 || Object.keys(arrayFields).length > 0) {
                        setInputType('form');
                        setFileNameField(null);
                        const initialData: Record<string, any> = {
                            ...Object.keys(templatedFields)
                                .reduce((acc, key) => {
                                    acc[key] = '';
                                    return acc;
                                }, {} as Record<string, any>),
                            ...Object.entries(arrayFields).reduce((acc, [key, value]) => {
                                acc[key] = value.map(() => '');
                                return acc;
                            }, {} as Record<string, any>)
                        };
                        setFormData(initialData);
                    } else {
                        // Use JSON form builder for complex payloads
                        setInputType('json');
                        setFileNameField(null);
                    }
                } catch (error) {
                    console.error('Error parsing payload:', error);
                    setInputType('json');
                    setFileNameField(null);
                }
            }
        }
    }, [action, actionSourceId]);

    // Set default selected agent when API response changes
    useEffect(() => {
        if (apiResponse?.data && Array.isArray(apiResponse.data) && apiResponse.data.length > 0) {
            setSelectedAgentIndex('view-all'); // Select "View All" by default
        }
    }, [apiResponse]);

    // Initialize comment data when API response changes
    useEffect(() => {
        if (apiResponse?.data) {
            const responseData = Array.isArray(apiResponse.data) ? apiResponse.data : [apiResponse.data];

            for (const item of responseData) {
                if (item.aiworker_usage_id) {
                    const messageId = item.aiworker_usage_id;

                    // Initialize comment state by replacing all comments
                    if (item.comment_history && item.comment_history.length > 0) {
                        dispatch(updateAgentMessageState({
                            messageId,
                            updates: { comments: item.comment_history }
                        }));
                    } else {
                        // Clear comments if no history
                        dispatch(updateAgentMessageState({
                            messageId,
                            updates: { comments: [] }
                        }));
                    }
                }
            }
        }
    }, [apiResponse, dispatch]);


    const handleInputChange = (key: string, value: string, arrayIndex?: number) => {
        setFormData(prev => {
            if (arrayIndex !== undefined && Array.isArray(prev[key])) {
                // Update array item
                const newArray = [...prev[key]];
                newArray[arrayIndex] = value;
                return {
                    ...prev,
                    [key]: newArray
                };
            }
            return {
                ...prev,
                [key]: value,
            };
        });
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files?.length) return;
        const fileList = Array.from(files);

        try {
            const payLoad = fileList.map((f) => ({
                filename: f.name,
                source_type: 'action',
            }));

            const uploadingFiles: UploadedFileType[] = fileList.map((f) => ({
                size: formatFileSize(f.size),
                name: f.name,
                type: getFileType(f.name),
                uploading: true,
                file: f,
            }));
            setUploadedFiles(uploadingFiles);
            setUploadedFile(uploadingFiles[0] ?? null);

            const uploadURL: any = await getAiworkersPresignedUrl(payLoad).unwrap();
            const urlList = Array.isArray(uploadURL) ? uploadURL : [uploadURL];
            await Promise.all(
                fileList.map((file, i) =>
                    uploadtos3({
                        file,
                        presignedUrl: urlList[i]?.presigned_url,
                        CtyShowToast
                    }).unwrap()
                )
            );

            await new Promise((resolve) => setTimeout(resolve, 2000));

            const filesInfo = urlList.map((r: any, i: number) => ({
                source_id: r?.source_id,
                source_extension: fileList[i]?.name.split('.').at(-1) ?? '',
                file_name: fileList[i]?.name ?? '',
            }));
            setUploadedFilesInfo(filesInfo);
            setExtraFieldsByFileIndex( new Array(filesInfo.length).fill(null).map(() => ({})));

            const completedFiles: UploadedFileType[] = fileList.map((f, i) => ({
                size: formatFileSize(f.size),
                name: f.name,
                type: getFileType(f.name),
                uploading: false,
                file: f,
            }));
            setUploadedFiles(completedFiles);
            setUploadedFile(completedFiles[0] ?? null);

            const first = filesInfo[0];
            const newFormData = {
                ...formData,
                source_id: first?.source_id,
                source_extension: first?.source_extension,
                file_name: first?.file_name,
            };
            setFormData(newFormData);
            const apiSpec = getApiSpec(action);
            const samplePayload = apiSpec?.sample_payload || apiSpec?.payload;
            const parsed = samplePayload ? safeParsePayload(samplePayload) : {};
            const { extraKeys } = getFileArrayTemplateInfo(parsed);
            if (extraKeys.length > 0) {
                CtyShowToast(checkType.success, t('Files uploaded. Fill extra fields and click Execute.'));
            }
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message || 'Failed to upload file');
        }
        event.target.value = '';
    };

    const handleExecute = async (submittedData?: any) => {
        try {
            if (inputType === 'json') {
                // For JSON form builder - use internal action API
                const apiSpec = getApiSpec(action);
                const samplePayload = apiSpec?.sample_payload || apiSpec?.payload;

                if (!samplePayload) {
                    CtyShowToast(checkType.error, t('No API payload configured'));
                    return;
                }

                const parsedPayload = safeParsePayload(samplePayload);

                // JsonFormBuilder returns data in nested structure matching the original payload
                // Use createNestedPayloadRecursive to properly merge submitted data with template
                const nestedPayload = createNestedPayloadRecursive(parsedPayload, submittedData || {});

                // Check if there's any actual data to send (not just empty structure)
                const hasData = (obj: any): boolean => {
                    if (obj === null || obj === undefined) return false;
                    if (typeof obj === 'string' && obj.trim() === '') return false;
                    if (Array.isArray(obj)) return obj.some(item => hasData(item));
                    if (typeof obj === 'object') {
                        return Object.values(obj).some(value => hasData(value));
                    }
                    return true;
                };

                if (!hasData(nestedPayload)) {
                    CtyShowToast(checkType.error, t('Please fill in at least one field'));
                    return;
                }

                const payload = {
                    source_id: action?.source_id,
                    payload: nestedPayload
                };

                const response = await actionUploadInternalAction(payload).unwrap();
                // Store response directly - it already has { data: [...] } structure
                setApiResponse(response);
                CtyShowToast(checkType.success, response?.message ?? t('Action executed successfully'));
            } else {
                // JSON tab — raw payload textarea
                if (inputType === 'form' && formInputModeTab === 'json') {
                    if (!rawPayloadText.trim()) {
                        CtyShowToast(checkType.error, t('Please enter a JSON payload'));
                        return;
                    }
                    let userPayload: Record<string, unknown>;
                    try {
                        const parsed = JSON.parse(rawPayloadText);
                        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                            CtyShowToast(checkType.error, t('Invalid JSON payload'));
                            return;
                        }
                        userPayload = parsed as Record<string, unknown>;
                    } catch {
                        CtyShowToast(checkType.error, t('Invalid JSON payload'));
                        return;
                    }

                    const apiSpec = getApiSpec(action);
                    const samplePayload = apiSpec?.sample_payload || apiSpec?.payload;
                    const nestedPayload = samplePayload
                        ? mergeTextareaPayloadWithTemplate(userPayload, samplePayload)
                        : userPayload;

                    const response = await actionUploadInternalAction({
                        source_id: action?.source_id,
                        payload: nestedPayload,
                    }).unwrap();
                    setApiResponse(response);
                    CtyShowToast(checkType.success, response?.message ?? t('Action executed successfully'));
                    return;
                }

                // For form inputs - use integrations API
                const hasAtLeastOneValue = Object.values(formData).some(v => (typeof v === 'string' ? v.trim() : v) !== '') || uploadedFilesInfo.length > 0;

                if (!hasAtLeastOneValue) {
                    CtyShowToast(checkType.error, t('Please fill in at least one field'));
                    return;
                }

                const apiSpec = getApiSpec(action);
                const samplePayload = apiSpec?.sample_payload || apiSpec?.payload;

                if (samplePayload) {
                    const parsedPayload = safeParsePayload(samplePayload);
                    const templatedFields = findTemplatedFields(parsedPayload);

                    const payloadData: Record<string, any> = { ...formData };
                    if ('file_name' in templatedFields && uploadedFile) {
                        payloadData['file_name'] = uploadedFile.name;
                    }

                    let nestedPayload = createNestedPayloadRecursive(parsedPayload, payloadData);
                    const { fileArrayKey, keysInTemplate } = getFileArrayTemplateInfo(parsedPayload);
                    if (fileArrayKey && uploadedFilesInfo.length > 0) {
                        const sourcesPayload = uploadedFilesInfo.map((fi: Record<string, any>, i: number) => {
                            const extra = extraFieldsByFileIndex[i] || {};
                            return keysInTemplate.reduce((acc, k) => {
                                const v = k in extra ? extra[k] : fi[k];
                                if (v !== undefined && v !== '') acc[k] = v;
                                return acc;
                            }, {} as Record<string, any>);
                        });
                        nestedPayload = { ...nestedPayload, [fileArrayKey]: sourcesPayload };
                    }

                    const integrationsPayload = {
                        source_id: action?.source_id,
                        payload: nestedPayload
                    };

                    const response = await actionUploadInternalAction(integrationsPayload).unwrap();
                    // Store response directly - it already has { data: [...] } structure
                    setApiResponse(response);
                    CtyShowToast(checkType.success, response?.message ?? t('Action executed successfully'));
                }
            }
        } catch (error: any) {
            // transformErrorResponse returns the response body directly:
            //   { status_code, message, data: [...] }
            // so `error` here IS that body — check error.data directly.
            const bodyData = error?.data;
            const hasNonEmptyData = Array.isArray(bodyData)
                ? bodyData.length > 0
                : bodyData && typeof bodyData === 'object' && Object.keys(bodyData).length > 0;
            if (hasNonEmptyData) {
                setApiResponse(error);
            }
            CtyShowToast(checkType.error, error?.message ?? (error as Error)?.message ?? t('Operation failed'));
        }
    };

    // Helper function to check if this is an Image Fraud Checker action
    const isImageFraudCheckerAction = () => {
        const actionName = action?.name?.toLowerCase();
        return actionName?.includes('image fraud') ||
            actionName?.includes('fraud check') ||
            actionName?.includes('image fraud checker');
    };

    // Helper function to handle file removal for Image Fraud Checker
    const handleRemoveFileForImageFraud = () => {
        setUploadedFile(null);
        setUploadedFiles([]);
        setUploadedFilesInfo([]);
        setExtraFieldsByFileIndex([]);
        setApiResponse(''); // Clear results state
        setPreviewUrl(null); // Clear preview URL
        setFormData(prev => {
            const newData = { ...prev };
            delete newData.source_id;
            // Keep source_extension field but reset it to empty to show upload section
            newData.source_extension = '';
            return newData;
        });
    };

    const handleExtraFieldChange = (fileIndex: number, fieldKey: string, value: string) => {
        setExtraFieldsByFileIndex((prev) => {
            const next = prev.slice();
            while (next.length <= fileIndex) next.push({});
            next[fileIndex] = { ...next[fileIndex], [fieldKey]: value };
            return next;
        });
    };

    // Helper functions to render file previews (reduces cognitive complexity)
    const renderImagePreview = (previewUrl: string, fileName: string) => {
        return (
            <img
                src={previewUrl}
                alt={fileName}
                className="w-full object-contain"
                style={{ minHeight: 'calc(100vh - 200px)' }}
            />
        );
    };

    const renderPdfPreview = (previewUrl: string, fileName: string) => {
        return (
            <div className="w-full border rounded-lg bg-default-100">
                <iframe
                    src={previewUrl}
                    className="w-full border-none"
                    title={fileName}
                    style={{ minHeight: 'calc(100vh - 200px)' }}
                />
            </div>
        );
    };

    const renderVideoPreview = (previewUrl: string) => {
        return (
            <div className="w-full border rounded-lg bg-black flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
                <video
                    src={previewUrl}
                    controls
                    className="w-full h-full"
                    style={{ maxHeight: 'calc(100vh - 200px)' }}
                >
                    <track kind="captions" />
                    {t('Your browser does not support video playback.')}
                </video>
            </div>
        );
    };

    const renderAudioPreview = (previewUrl: string, fileName: string, fileSize: string) => {
        return (
            <div className="w-full border rounded-lg bg-default-100 flex flex-col items-center justify-center p-8" style={{ minHeight: 'calc(100vh - 200px)' }}>
                <Icon icon="mdi:music-circle" className="w-24 h-24 text-primary mb-6" />
                <p className="text-lg font-medium text-default-600 mb-2">{fileName}</p>
                <p className="text-sm text-default-500 mb-6">{fileSize}</p>
                <audio
                    src={previewUrl}
                    controls
                    className="w-full max-w-md"
                >
                    <track kind="captions" />
                    {t('Your browser does not support audio playback.')}
                </audio>
            </div>
        );
    };

    const renderTextFilePreview = (previewUrl: string, fileName: string, fileSize: string, fileExtension: string) => {
        return (
            <TextFilePreview previewUrl={previewUrl} fileName={fileName} fileSize={fileSize} fileExtension={fileExtension} />
        );
    };

    const renderUnsupportedFilePreview = (fileName: string, fileSize: string) => {
        return (
            <div className="w-full border rounded-lg bg-default-100 flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
                <div className="text-center">
                    <Icon icon="mdi:file-document" className="w-16 h-16 text-default-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-default-600">{fileName}</p>
                    <p className="text-sm text-default-500">{fileSize}</p>
                    <p className="text-xs text-default-400 mt-2">{t('Preview not available for this file type')}</p>
                </div>
            </div>
        );
    };

    const renderFilePreview = () => {
        if (!uploadedFile || !previewUrl) return null;

        const fileExtension = uploadedFile.name.toLowerCase().split('.').pop() || '';
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif'];
        const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'm4v', 'wmv', 'mpeg', 'mpg'];
        const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma', 'opus'];
        const textExtensions = ['txt', 'json', 'xml', 'csv', 'log', 'md', 'yaml', 'yml', 'html', 'css', 'js', 'ts', 'tsx', 'jsx'];

        if (imageExtensions.includes(fileExtension)) {
            return renderImagePreview(previewUrl, uploadedFile.name);
        }
        if (fileExtension === 'pdf') {
            return renderPdfPreview(previewUrl, uploadedFile.name);
        }
        if (videoExtensions.includes(fileExtension)) {
            return renderVideoPreview(previewUrl);
        }
        if (audioExtensions.includes(fileExtension)) {
            return renderAudioPreview(previewUrl, uploadedFile.name, uploadedFile.size);
        }
        if (textExtensions.includes(fileExtension)) {
            return renderTextFilePreview(previewUrl, uploadedFile.name, uploadedFile.size, fileExtension);
        }
        return renderUnsupportedFilePreview(uploadedFile.name, uploadedFile.size);
    };

    const renderAgentExecutionContent = (item: any) => {
        const onButtonAction = (actionType: string, buttonData: any) => {
            console.log('Button action:', actionType, buttonData);
        };
        const isClaimScoreAgent = item?.agent_name === "Claim Score Generator";
        const isVHR = item?.agent_name?.toLowerCase().includes("vhr");
        const isInvoiceMatcher = item?.agent_name?.toLowerCase().includes("invoice matcher")
            || item?.agent_name?.toLowerCase().includes("ro matcher")
            || item?.agent_name?.toLowerCase().includes("maintenance claim");

        if (isInvoiceMatcher) {
            return (
                <InvoiceMatcherRenderer
                    claimData={[item]}
                    showActionButtons={true}
                    source="agent_history"
                    onButtonAction={onButtonAction}
                />
            );
        }
        if (isClaimScoreAgent && isVHR) {
            return (
                <ClaimScoreGeneratorRenderer
                    claimData={[item]}
                    showActionButtons={true}
                    source="agent_history"
                    onButtonAction={onButtonAction}
                />
            );
        }
        return (
            <ClaimDataRenderer
                claimData={[item]}
                showActionButtons={true}
                onButtonAction={onButtonAction}
            />
        );
    };
    const getUxGenieApiResponseData = (apiResponse: any) => {
        if (apiResponse?.data && Array.isArray(apiResponse?.data) && apiResponse?.data?.length > 0) {
            return apiResponse?.data?.[0];
        }else if (apiResponse?.data && typeof apiResponse?.data === 'object') {
            return apiResponse?.data;
        }
        return apiResponse;
    };

    const renderFormInput = () => {
        // If this is an Image Fraud Checker action and file is uploaded, show specialized UI
        if (isImageFraudCheckerAction() && uploadedFile && previewUrl) {
            return (
                <div className="container-view flex-col gap-3 pb-3 bg-greken-300">
                    <ImageFraudChecker
                        uploadedFile={uploadedFile}
                        previewUrl={previewUrl}
                        apiResponse={Array.isArray(apiResponse?.data) ? apiResponse.data : apiResponse?.data ?? apiResponse}
                        isLoading={isLoading || isActionLoading}
                        isUploading={uploadedFile?.uploading || false}
                        onRemoveFile={handleRemoveFileForImageFraud}
                    />
                    <Divider className="my-1" />
                    <div className="flex justify-start p-3">
                        <CtyButton
                            variant="solid"
                            color="primary"
                            size="md"
                            radius="sm"
                            onPress={() => handleExecute()}
                            isLoading={isActionLoading || isLoading}
                            isDisabled={!canUpdate || isActionLoading || isLoading || uploadedFile?.uploading}
                        >
                            {t('Execute')}
                        </CtyButton>
                    </div>
                </div>
            );
        }
        // Otherwise show the normal form UI
        const apiSpec = getApiSpec(action);
        const samplePayload = apiSpec?.sample_payload || apiSpec?.payload;
        const parsedPayloadForFields = samplePayload ? safeParsePayload(samplePayload) : {};
        const { extraKeys } = getFileArrayTemplateInfo(parsedPayloadForFields);
        const extraKeysForFile = inputType === 'file' && uploadedFiles.length > 0 ? extraKeys : [];
        const extraKeysSet = new Set(extraKeysForFile);

        const renderStructuredFields = () => (
            <div className="flex flex-col gap-2">
                {Object.keys(formData).map((key) => {
                    if (key === 'source_id') return null;

                    if (inputType === 'file' && (key === 'file_name' || key === fileNameField)) return null;

                    if (inputType === 'file' && uploadedFiles.length > 0 && extraKeysSet.has(key)) return null;

                    if (key === 'source_extension') {
                        const allowedExtensions = formData[key] ? [formData[key]] : ['*'];
                        return (
                            <div key={key}>
                                <label className="flex flex-col items-center justify-center w-full p-6 border-2 my-2 border-dashed border-default-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/10 transition-colors">
                                    {uploadedFiles.length > 0 ? (
                                        <div className="flex flex-col gap-2 w-full">
                                            {uploadedFiles.map((file, idx) => (
                                                <div key={`${file.name}-${idx}`} className="border rounded-lg p-2 bg-default-100 !w-full">
                                                    <FileUpload file={file} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center">
                                            <Icon icon="material-symbols:upload" />
                                            <p className="text-sm text-default-500">
                                                <span className="font-semibold">{t('Upload Files')}</span>
                                                {allowedExtensions[0] !== '*' && (
                                                    <span className="block text-xs">
                                                        Allowed: {allowedExtensions.join(', ')}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    <Input
                                        type="file"
                                        id="upload-file-input"
                                        multiple
                                        accept={allowedExtensions[0] === '*' ? '*' : allowedExtensions.map(ext => `.${ext}`).join(',')}
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                        );
                    }

                    return (
                        <div className="flex flex-col gap-1" key={key}>
                            {Array.isArray(formData[key]) ? (
                                <>
                                    <label htmlFor={key}>
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(key)}
                                        </span>
                                    </label>
                                    <div className="flex flex-col gap-2">
                                        {formData[key].map((item: string, index: number) => (
                                            <Input
                                                key={`${key}-${index}`}
                                                id={`${key}-${index}`}
                                                type="text"
                                                size="sm"
                                                aria-label={`${key} ${index + 1}`}
                                                value={item}
                                                placeholder={`Enter ${key} ${index + 1}`}
                                                isRequired={true}
                                                onValueChange={(value) => handleInputChange(key, value, index)}
                                            />
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <label htmlFor={key}>
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(key)}
                                        </span>
                                    </label>
                                    <div className="flex-1">
                                        <Input
                                            id={key}
                                            type="text"
                                            size="sm"
                                            aria-label={key}
                                            value={formData[key]}
                                            placeholder={`Enter ${key}`}
                                            isRequired={true}
                                            onValueChange={(value) => handleInputChange(key, value)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
                {inputType === 'file' && uploadedFiles.length > 0 && extraKeysForFile.length > 0 && (
                    <div className="flex flex-col gap-2 mt-3">
                        {extraKeysForFile.map((extraKey) => (
                            <div key={extraKey} className="flex flex-col gap-1">
                                <label htmlFor={`${extraKey}-0`}>
                                    <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                        {t(extraKey)}
                                    </span>
                                </label>
                                <div className="flex flex-col gap-2">
                                    {uploadedFiles.map((_, idx) => (
                                        <Input
                                            key={`${extraKey}-${idx}`}
                                            id={`${extraKey}-${idx}`}
                                            type="text"
                                            size="sm"
                                            aria-label={uploadedFiles.length > 1 ? `${extraKey} ${idx + 1}` : extraKey}
                                            value={extraFieldsByFileIndex[idx]?.[extraKey] ?? ''}
                                            placeholder={uploadedFiles.length > 1 ? `Enter ${extraKey} ${idx + 1}` : `Enter ${extraKey}`}
                                            onValueChange={(v) => handleExtraFieldChange(idx, extraKey, v)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );

        return (
            <div className="flex flex-col gap-3 pb-3 bg-greken-300 w-full">
                <div className="flex flex-col md:flex-row gap-3 w-full items-start">
                    <div className="md:w-[30%] w-full shrink-0">
                        <div className="px-3">
                            {inputType === 'form' ? (
                                <Tabs
                                    selectedKey={formInputModeTab}
                                    onSelectionChange={(key) => setFormInputModeTab(key as 'form' | 'json')}
                                    aria-label={t('Input mode')}
                                    variant="underlined"
                                    size="sm"
                                    classNames={{
                                        base: 'w-full',
                                        tabList: 'gap-4 relative rounded-none px-0 pb-0',
                                        cursor: 'w-full bg-primary',
                                        tab: 'max-w-fit px-2 h-8',
                                        tabContent: 'group-data-[selected=true]:text-primary',
                                        panel: 'pt-2 pb-0 px-0',
                                    }}
                                >
                                    <Tab key="form" title={t('Form')}>
                                        {renderStructuredFields()}
                                    </Tab>
                                    <Tab key="json" title={t('JSON')}>
                                        <Textarea
                                            minRows={8}
                                            value={rawPayloadText}
                                            onValueChange={setRawPayloadText}
                                            placeholder={t('Please enter complete payload')}
                                            aria-label={t('JSON payload')}
                                            classNames={{ input: 'font-mono text-sm' }}
                                        />
                                    </Tab>
                                </Tabs>
                            ) : (
                                renderStructuredFields()
                            )}
                            <Divider className="my-2" />
                            <div className="flex justify-end">
                                <CtyButton
                                    variant="solid"
                                    color="primary"
                                    size="md"
                                    radius="sm"
                                    onPress={() => handleExecute()}
                                    isLoading={isActionLoading || isLoading}
                                    isDisabled={!canUpdate || isActionLoading || isLoading || uploadedFiles.some(f => f?.uploading)}
                                >
                                    {t('Execute')}
                                </CtyButton>
                            </div>

                            {uploadedFile && (
                                <div className="w-full mt-3">
                                    {renderFilePreview()}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="md:flex-1 w-full min-w-0 container-view flex-col">
                        <div className="container-scrollable pr-3">
                                <CacheHitNotice cache={apiResponse?.cache} />
                                {(apiResponse && Array.isArray(apiResponse?.data) && apiResponse?.data.length === 0) ? (
                                    <div className='text-center text-default-500 flex items-center justify-center h-[40vh] '>No data found for this claim</div>
                                ) : null}
                                {apiResponse && apiResponse?.data && action?.name === "Lifecycle of Claim" ? (
                                    <div className="flex flex-col gap-4">
                                        {/* Dropdown selector */}
                                        <div className="flex flex-col gap-3">
                                            <label htmlFor="agent-select" className="text-sm font-semibold text-default-700 tracking-wide">Select Agent:</label>
                                            <div className="relative">
                                                <select
                                                    id="agent-select"
                                                    className="w-full px-4 py-3 bg-content1 border-2 border-default-200 rounded-xl text-default-600 font-medium appearance-none cursor-pointer transition-all duration-200 hover:border-primary focus:border-primary focus:outline-none"
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value === 'view-all') {
                                                            setSelectedAgentIndex('view-all');
                                                        } else {
                                                            const selectedIndex = Number.parseInt(value, 10);
                                                            setSelectedAgentIndex(selectedIndex);
                                                        }
                                                    }}
                                                    value={selectedAgentIndex}
                                                >
                                                    <option value="view-all" className="font-semibold text-primary">View All</option>
                                                    {Array.isArray(apiResponse?.data) && apiResponse?.data.map((item: any, index: number) => {
                                                        const scoreDisplay = !item.agent_score || item.agent_score === 'None' ? 0 : item.agent_score;
                                                        return (
                                                        <option key={`${item.agent_name}-${index}`} value={index}>
                                                            {item.agent_name} {item.agent_score ? `(Score: ${scoreDisplay})` : ''}
                                                        </option>
                                                        );
                                                    })}
                                                </select>
                                                {/* Custom dropdown arrow */}
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Display selected item */}
                                        {selectedAgentIndex === 'view-all' ? (
                                            // View All mode - show all items in stacked layout
                                            <div className="space-y-4">
                                                {Array.isArray(apiResponse?.data) && apiResponse?.data.map((item: any, index: number) => {
                                                    const scoreDisplay = !item.agent_score || item.agent_score === 'None' ? 0 : item.agent_score;
                                                    return (
                                                    <div key={index} className="bg-content1 shadow-md rounded-lg p-4">
                                                        <div className="text-lg font-semibold mb-4">
                                                            {item.agent_name}
                                                            {item.agent_score && <span className="ml-2 text-default-500">(Score: {scoreDisplay})</span>}
                                                        </div>
                                                        {renderAgentExecutionContent(item)}
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        ) : typeof selectedAgentIndex === 'number' && selectedAgentIndex >= 0 && apiResponse?.data?.[selectedAgentIndex] ? (
                                            (() => {
                                                const item = apiResponse.data[selectedAgentIndex];
                                                return (
                                                    <div className="bg-content1 shadow-md rounded-lg p-4">
                                                        {renderAgentExecutionContent(item)}
                                                    </div>
                                                );
                                            })()
                                        ) : null}
                                    </div>
                                ) : apiResponse ? (
                                    <div>
                                        {useCustomTemplate && savedTemplate ? (
                                            <div className="p-4 border rounded-lg bg-content1 shadow-sm">
                                                <UXGenieTemplateRenderer
                                                    code={savedTemplate.code}
                                                    data={getUxGenieApiResponseData(apiResponse)}
                                                />
                                            </div>
                                        ) : (
                                            <JsonFormBuilder
                                                jsonData={(() => {
                                                    const data = apiResponse?.data || apiResponse;
                                                    // Filter out status_code and message fields
                                                    if (typeof data === 'object' && data !== null) {
                                                        const filteredData = { ...data };
                                                        delete filteredData.status_code;
                                                        delete filteredData.message;
                                                        return filteredData;
                                                    }
                                                    return data;
                                                })()}
                                                readOnly={true}
                                                showSubmitButton={false}
                                            />
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
        );
    };

    const renderJsonFormInput = () => {
        const apiSpec = getApiSpec(action);
        const samplePayload = apiSpec?.sample_payload || apiSpec?.payload;
        if (!samplePayload) return null;

        try {
            const parsedPayload = safeParsePayload(samplePayload);
            const templatedFields = findTemplatedFields(parsedPayload);
            console.log('templatedFields', templatedFields);
            return (
                <div className="flex gap-4 w-full max-h-[80vh]">
                    <div className="flex-1 min-w-0 overflow-y-auto border-r pr-2">
                        <JsonFormBuilder
                            jsonData={parsedPayload}
                            readOnly={false}
                            showSubmitButton={true}
                            onSubmit={handleExecute}
                            submitButtonText="Execute"
                            isDisabled={!canUpdate}
                        />
                    </div>
                    <div className="flex-1 min-w-0 overflow-y-auto pl-2">
                        <CacheHitNotice cache={apiResponse?.cache} />
                        {apiResponse && (
                            useCustomTemplate && savedTemplate ? (
                                <div className="p-4 border rounded-lg bg-content1 shadow-sm min-h-full">
                                    <UXGenieTemplateRenderer
                                        code={savedTemplate.code}
                                        data={apiResponse?.data ?? apiResponse}
                                    />
                                </div>
                            ) : (
                                <JsonFormBuilder
                                    jsonData={apiResponse?.data || apiResponse}
                                    readOnly={true}
                                    showSubmitButton={false}
                                />
                            )
                        )}
                    </div>
                </div>
            );
        } catch (error) {
            return <div className="text-danger">Error parsing API payload</div>;
        }
    };

    // Render based on input type
    const renderContent = () => {
        switch (inputType) {
            case 'file':
            case 'form':
                return renderFormInput();
            case 'json':
                return renderJsonFormInput();
            default:
                return <div>Loading...</div>;
        }
    };

    return (
        <div className="flex flex-col w-full">
            {/* Template Toggle - Show when template exists or is loading */}
            {(savedTemplate || isLoadingTemplate) && (
                <div className="flex items-center justify-end gap-2 mb-4 p-3 rounded-lg">
                    <Icon icon="material-symbols:view-module" className="text-primary" width={20} />
                    {isLoadingTemplate ? (
                        <div className="flex items-center gap-2 text-default-500">
                            <Icon icon="eos-icons:loading" className="animate-spin" width={20} />
                            <span className="text-sm">Loading template...</span>
                        </div>
                    ) : (
                        <Select
                            placeholder="Select Response View"
                            selectedKeys={[useCustomTemplate ? "custom" : "json"]}
                            onChange={(e: any) => setUseCustomTemplate(e.target.value === 'custom')}
                            className="max-w-xs"
                            aria-label="Response View Selector"
                        >
                            <SelectItem key="json" value="json">JSON Viewer</SelectItem>
                            <SelectItem key="custom" value="custom">UXGenie Template</SelectItem>
                        </Select>
                    )}
                </div>
            )}

            {renderContent()}
        </div>
    );
} 